<template>
  <div class="stats-container" :class="{ 'team-away': teamType === 2 }">
    <div class="header">
      <h1 class="title">{{ teamTitle }}进攻统计</h1>
    </div>

    <div class="stats-content">
      <!-- 进攻统计 -->
      <div class="stat-card">
        <div class="stat-label">进攻</div>
        <div class="stat-controls">
          <button 
            class="btn btn-minus" 
            @click="decreaseAttacks"
            :disabled="attacks <= 0"
          >
            <span class="btn-icon">−</span>
          </button>
          <div class="stat-value">{{ attacks }}</div>
          <button 
            class="btn btn-plus" 
            @click="increaseAttacks"
          >
            <span class="btn-icon">+</span>
          </button>
        </div>
      </div>

      <!-- 危险进攻统计 -->
      <div class="stat-card">
        <div class="stat-label">危险进攻</div>
        <div class="stat-controls">
          <button 
            class="btn btn-minus" 
            @click="decreaseDangerousAttacks"
            :disabled="dangerousAttacks <= 0"
          >
            <span class="btn-icon">−</span>
          </button>
          <div class="stat-value">{{ dangerousAttacks }}</div>
          <button 
            class="btn btn-plus" 
            @click="increaseDangerousAttacks"
          >
            <span class="btn-icon">+</span>
          </button>
        </div>
      </div>

      <!-- 重置按钮 -->
      <div class="reset-section">
        <button class="btn-reset" @click="resetStats">重置</button>
      </div>
      
      <!-- 保存提示 -->
      <transition name="fade">
        <div v-if="saveTip" class="save-tip">已保存</div>
      </transition>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'

export default {
  name: 'JingongAndweixianjingong',
  setup() {
    const route = useRoute()
    const attacks = ref(0)
    const dangerousAttacks = ref(0)
    const saveTip = ref(false)
    const loading = ref(false)
    let saveTipTimer = null
    
    // 存储 team_id 和 match_id
    const teamId = ref(null)
    const matchId = ref(null)

    // 获取队伍类型，默认是1（主队），2是客队
    const teamType = computed(() => {
      const type = route.query.type || route.params.type || '1'
      return parseInt(type) === 2 ? 2 : 1
    })

    // 队伍标题
    const teamTitle = computed(() => {
      return teamType.value === 2 ? '客队' : '主队'
    })

    // 获取存储键名
    const getStorageKey = (key) => {
      const prefix = teamType.value === 2 ? 'away' : 'home'
      return `${prefix}_${key}`
    }

    // 获取统计数据
    const fetchStatistics = async () => {
      const currentMatchId = route.query.match_id || route.params.match_id
      if (!currentMatchId) {
        console.warn('未找到 match_id，跳过获取统计数据')
        return
      }
      
      matchId.value = currentMatchId
      loading.value = true
      
      try {
        const response = await fetch(`https://api.aiyqzx.com/app/v1/other/match_statistics?match_id=${currentMatchId}`)
        const result = await response.json()
        
        if (result.code === 1 && result.data) {
          // 根据队伍类型获取对应的数据
          const teamData = teamType.value === 2 ? result.data.team_B : result.data.team_A
          
          if (teamData) {
            teamId.value = teamData.team_id
            attacks.value = teamData.attacks || 0
            dangerousAttacks.value = teamData.dangerous_attacks || 0
          }
        }
      } catch (error) {
        console.error('获取统计数据失败:', error)
      } finally {
        loading.value = false
      }
    }

    // 上传统计数据
    const uploadStatistics = async () => {
      if (!matchId.value || !teamId.value) {
        console.warn('缺少 match_id 或 team_id，无法上传')
        return
      }
      
      try {
        const response = await fetch('https://api.aiyqzx.com/app/v1/other/statistics', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            match_id: matchId.value,
            team_id: teamId.value,
            attacks: attacks.value,
            dangerous_attacks: dangerousAttacks.value
          })
        })
        
        const result = await response.json()
        if (result.code !== 1) {
          console.error('上传统计数据失败:', result.msg)
        }
      } catch (error) {
        console.error('上传统计数据失败:', error)
      }
    }

    // 保存数据到本地存储并上传
    const saveStats = async () => {
      try {
        localStorage.setItem(getStorageKey('attacks'), attacks.value.toString())
        localStorage.setItem(getStorageKey('dangerousAttacks'), dangerousAttacks.value.toString())
        
        // 上传数据
        await uploadStatistics()
        
        // 显示保存提示
        saveTip.value = true
        if (saveTipTimer) {
          clearTimeout(saveTipTimer)
        }
        saveTipTimer = setTimeout(() => {
          saveTip.value = false
        }, 1500)
      } catch (error) {
        console.error('保存数据失败:', error)
        alert('保存数据失败，请检查浏览器设置')
      }
    }

    // 增加进攻
    const increaseAttacks = () => {
      attacks.value++
      saveStats()
    }

    // 减少进攻
    const decreaseAttacks = () => {
      if (attacks.value > 0) {
        attacks.value--
        saveStats()
      }
    }

    // 增加危险进攻
    const increaseDangerousAttacks = () => {
      dangerousAttacks.value++
      saveStats()
    }

    // 减少危险进攻
    const decreaseDangerousAttacks = () => {
      if (dangerousAttacks.value > 0) {
        dangerousAttacks.value--
        saveStats()
      }
    }

    // 重置统计
    const resetStats = () => {
      if (confirm(`确定要重置${teamTitle.value}的所有统计数据吗？`)) {
        attacks.value = 0
        dangerousAttacks.value = 0
        saveStats()
      }
    }

    onMounted(async () => {
      // 先获取服务器数据
      await fetchStatistics()
    })

    return {
      teamType,
      teamTitle,
      attacks,
      dangerousAttacks,
      saveTip,
      loading,
      increaseAttacks,
      decreaseAttacks,
      increaseDangerousAttacks,
      decreaseDangerousAttacks,
      resetStats
    }
  }
}
</script>

<style scoped>
.stats-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #3B82F6 0%, #2563EB 100%);
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: background 0.3s ease;
}

.stats-container.team-away {
  background: linear-gradient(135deg, #f37739 0%, #e85d2e 100%);
}

.header {
  width: 100%;
  max-width: 500px;
  margin-bottom: 40px;
}

.title {
  font-size: 32px;
  font-weight: bold;
  color: white;
  text-align: center;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
  margin: 0;
}

.stats-content {
  width: 100%;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.stat-card {
  background: white;
  border-radius: 20px;
  padding: 30px 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 25px;
}

.stat-label {
  font-size: 28px;
  font-weight: 600;
  color: #333;
  text-align: center;
}

.stat-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 30px;
  width: 100%;
}

.btn {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  border: none;
  font-size: 40px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.btn:active {
  transform: scale(0.95);
}

.btn-minus {
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%);
  color: white;
}

.btn-minus:disabled {
  background: #ccc;
  cursor: not-allowed;
  opacity: 0.6;
}

.btn-minus:not(:disabled):hover {
  background: linear-gradient(135deg, #ee5a6f 0%, #ff6b6b 100%);
  box-shadow: 0 6px 20px rgba(238, 90, 111, 0.4);
}

.btn-plus {
  background: linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%);
  color: white;
}

.btn-plus:hover {
  background: linear-gradient(135deg, #44a08d 0%, #4ecdc4 100%);
  box-shadow: 0 6px 20px rgba(78, 205, 196, 0.4);
}

.btn-icon {
  line-height: 1;
  font-weight: 300;
}

.stat-value {
  font-size: 64px;
  font-weight: bold;
  color: #333;
  min-width: 100px;
  text-align: center;
  font-variant-numeric: tabular-nums;
}

.reset-section {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 10px;
}

.btn-reset {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 2px solid white;
  border-radius: 25px;
  padding: 12px 40px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.btn-reset:active {
  transform: scale(0.95);
}

.btn-reset:hover {
  background: rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.save-tip {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 15px 30px;
  border-radius: 25px;
  font-size: 18px;
  font-weight: 600;
  z-index: 1000;
  pointer-events: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* 移动端适配 */
@media (max-width: 480px) {
  .stats-container {
    padding: 15px;
  }

  .title {
    font-size: 26px;
    margin-bottom: 30px;
  }

  .stat-card {
    padding: 25px 15px;
    gap: 20px;
  }

  .stat-label {
    font-size: 24px;
  }

  .stat-value {
    font-size: 56px;
  }

  .btn {
    width: 60px;
    height: 60px;
    font-size: 36px;
  }

  .stat-controls {
    gap: 25px;
  }
}

/* 防止双击缩放 */
@media (max-width: 768px) {
  .btn {
    touch-action: manipulation;
  }
}
</style>

